import { FC, memo, useMemo } from 'react'

import { useFragment } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Link } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { ExpensesMileageExpensableApprovableFragmentDoc } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import Attachments from 'modules/inbox_tasks/components/Attachments'
import { cleanDocuments } from 'modules/inbox_tasks/tasks/approval/ExpensesExpensable/helpers'
import { ExpensableTypeComponentProps } from 'modules/inbox_tasks/tasks/approval/ExpensesExpensable/types'

const MileageExpensableDetails: FC<ExpensableTypeComponentProps> = ({
  id,
  info,
  moreDetails,
  commonDetails,
}) => {
  const { data } = useFragment({
    fragment: ExpensesMileageExpensableApprovableFragmentDoc,
    fragmentName: 'ExpensesMileageExpensableApprovable',
    from: { __typename: 'ExpensesExpensable', id },
  })

  const mileage = data?.mileage

  const details = useMemo(
    () =>
      [
        ...commonDetails,
        {
          title: i18n.t('expenses_v2.forms.fields.category.label'),
          content: {
            type: 'item' as const,
            text: mileage?.category?.name || '-',
          },
        },
        {
          title: i18n.t('dashboard.todo.approvals.expenses.mileage_rate'),
          content: {
            type: 'item' as const,
            text: mileage?.rate || '-',
          },
        },
        {
          title: i18n.t('dashboard.todo.approvals.expenses.total_mileage'),
          content: {
            type: 'item' as const,
            text:
              mileage?.mileage !== undefined && mileage?.mileage !== null
                ? `${Number(mileage.mileage) / 100} ${mileage.units ?? ''}`
                : '-',
          },
        },
        info.amount && {
          title: i18n.t('expenses_v2.forms.fields.amount.label'),
          content: {
            type: 'item' as const,
            text: info.amount,
          },
        },
        info.reimbursableAmount && {
          title: i18n.t('expenses_v2.forms.fields.reimbursable_amount.label'),
          content: {
            type: 'item' as const,
            text: info.reimbursableAmount,
          },
        },
        {
          title: i18n.t('expenses_v2.forms.fields.effective_on.label'),
          content: {
            type: 'item' as const,
            text: info.effectiveOn,
          },
        },
      ].filter(Boolean),
    [commonDetails, mileage, info]
  )

  return (
    <>
      <DetailsItemsList details={details} tableView />
      <Box paddingBottom="s4">
        <F0Link href={moreDetails.url}>{moreDetails.title}</F0Link>
      </Box>
      {!!mileage?.files && !!mileage.files.length && (
        <Box flexDirection="column" gap="s16">
          <Text.Header3>
            {i18n.t('expenses_v2.confirmation_screen.expense.attached_documents_block.title')}
          </Text.Header3>
          <Attachments files={cleanDocuments(mileage.files)} />
        </Box>
      )}
    </>
  )
}

export default memo(MileageExpensableDetails)
